// Deterministic currency-string <-> integer minor units. NO binary-float money authority (never
// Math.round(Number(input) * 100)). Generic over currency: the number of minor-unit digits comes from Intl for the
// operation's own currency (USD/CAD -> 2, JPY -> 0, etc.), so the same confirmation contract works for any tenant,
// product, amount, and Servisfy-supported currency. Zero-width / bidi format chars (paste & autocorrect noise that
// break parseFloat) are stripped first. Unparseable input -> null, which callers distinguish from an amount mismatch.

// Strip zero-width / bidi format code points (U+200B..U+200F, U+2060, U+FEFF, U+202A..U+202E, U+2066..U+2069).
// Numeric checks avoid embedding invisible characters in source (which break source parsers).
function stripInvisible(str) {
  let out = '';
  for (const ch of str) {
    const c = ch.codePointAt(0);
    if (
      (c >= 0x200b && c <= 0x200f) ||
      c === 0x2060 ||
      c === 0xfeff ||
      (c >= 0x202a && c <= 0x202e) ||
      (c >= 0x2066 && c <= 0x2069)
    ) {
      continue;
    }
    out += ch;
  }
  return out;
}

// Minor-unit digits for a currency (2 for USD/CAD, 0 for JPY, ...). Falls back to 2 if Intl can't resolve it.
export function currencyFractionDigits(currency) {
  try {
    const d = new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' })
      .resolvedOptions().maximumFractionDigits;
    return Number.isInteger(d) ? d : 2;
  } catch {
    return 2;
  }
}

// Deterministic decimal string -> integer minor units for the given currency. Accepts a leading "$" and surrounding
// whitespace; rejects comma decimals, >digits decimals, negatives, letters, empty. Returns null on any parse failure.
export function parseMoneyToMinorUnits(input, currency) {
  const digits = currencyFractionDigits(currency);
  const s = stripInvisible(String(input == null ? '' : input))
    .trim()
    .replace(/^\$/, '')
    .replace(/\s+/g, '');
  if (digits === 0) {
    const m = /^(\d+)$/.exec(s);
    return m ? parseInt(m[1], 10) : null;
  }
  const m = new RegExp(`^(\\d+)(?:\\.(\\d{1,${digits}}))?$`).exec(s);
  if (!m) return null;
  const whole = parseInt(m[1], 10);
  const fracStr = ((m[2] || '') + '0'.repeat(digits)).slice(0, digits);
  return whole * 10 ** digits + parseInt(fracStr || '0', 10);
}

// Integer minor units -> the plain decimal string the human must type to confirm (e.g. 1995/USD -> "19.95",
// 500/USD -> "5.00", 29900/USD -> "299.00", 500/JPY -> "500"). Display only; authority stays integer.
export function minorUnitsToDecimalString(cents, currency) {
  const digits = currencyFractionDigits(currency);
  const n = Number(cents || 0);
  return digits === 0 ? String(n) : (n / 10 ** digits).toFixed(digits);
}

// Integer minor units -> localized currency display (e.g. 1995/USD -> "$19.95"). Display only.
export function formatMoney(cents, currency) {
  const cur = currency || 'USD';
  const digits = currencyFractionDigits(cur);
  const n = Number(cents || 0) / 10 ** digits;
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: cur }).format(n);
  } catch {
    return `${minorUnitsToDecimalString(cents, cur)} ${cur}`;
  }
}

// Back-compat USD convenience (existing callers/tests): "19.95" -> 1995.
export function dollarsToCents(input) {
  return parseMoneyToMinorUnits(input, 'USD');
}

export default dollarsToCents;
